/**
 * After teachers edit AMCQ keys in the question bank, re-score stored submissions
 * (grades + in-progress progress) so student answers are checked against the new correct choice.
 */
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';
import { getAmcqQuestionsForScoring } from './amcqPanelGraph';
import { irtScoreBundle } from './irtMst';

const difficultyBandFromScore = (score) => {
  const n = parseFloat(score);
  if (Number.isNaN(n)) return 'Medium';
  if (n <= 0.9) return 'Easy';
  if (n <= 1.9) return 'Medium';
  return 'Hard';
};

const assignmentForIrt = (a) =>
  a ? { ...a, questions: getAmcqQuestionsForScoring(a) } : a;

function mergeQuestionBankIntoScoringPool(assignment, savedQuestions) {
  if (!assignment || !Array.isArray(savedQuestions)) return savedQuestions || [];
  const flat = assignment.amcqQuestionsFlat;
  if (Array.isArray(flat) && flat.length > 0) {
    return flat.map((fq) => {
      const match = savedQuestions.find(
        (sq) =>
          (sq?.question && fq?.question && sq.question === fq.question) ||
          (sq?.questionId != null &&
            fq?.questionId != null &&
            String(sq.questionId) === String(fq.questionId))
      );
      return match ? { ...fq, ...match } : fq;
    });
  }
  return savedQuestions;
}

export function assignmentForAmcqRegrade(assignment, savedQuestions) {
  const merged = mergeQuestionBankIntoScoringPool(assignment, savedQuestions);
  const next = { ...assignment, questions: savedQuestions };
  if (Array.isArray(assignment?.amcqQuestionsFlat) && assignment.amcqQuestionsFlat.length > 0) {
    next.amcqQuestionsFlat = merged;
  }
  return next;
}

const findQuestionInPool = (pool, questionStem) =>
  (pool || []).find((q) => q && q.question === questionStem) || null;

function formatAmcqGradedQuestion(question, selectedChoice, meta = {}) {
  if (!question) {
    return {
      question: '',
      selectedChoice,
      choices: [],
      explanations: [],
      correctChoice: '',
      ...(typeof meta.order === 'number' && !Number.isNaN(meta.order) ? { order: meta.order } : {}),
    };
  }
  return {
    questionId: question.questionId || question.question,
    question: question.question,
    ...(question.sectorNumber != null && question.sectorNumber !== ''
      ? { sectorNumber: question.sectorNumber }
      : {}),
    ...(question.difficultyScore != null &&
    question.difficultyScore !== '' &&
    !Number.isNaN(parseFloat(question.difficultyScore))
      ? {
          difficultyScore: question.difficultyScore,
          difficultyBand: difficultyBandFromScore(question.difficultyScore),
        }
      : {}),
    selectedChoice,
    correctChoice: question.correctChoice || '',
    choices: question.choices || [],
    explanations: question.explanations || [],
    ...(typeof meta.order === 'number' && !Number.isNaN(meta.order) ? { order: meta.order } : {}),
  };
}

/** Map question stem -> latest student letter + order (from saved grade/progress shape). */
function collectStudentResponses(stored) {
  const byStem = new Map();
  const add = (item) => {
    if (!item || !item.question) return;
    const letter = String(
      item.selectedChoice != null && item.selectedChoice !== ''
        ? item.selectedChoice
        : item.chosenAnswer || item.correctChoice || ''
    ).toLowerCase();
    byStem.set(item.question, { letter, order: item.order });
  };
  for (const item of stored.correctQuestions || []) add(item);
  for (const item of stored.incorrectQuestions || []) add(item);
  return byStem;
}

export function recalculateAmcqSubmissionFromAssignment(assignment, savedQuestions, stored) {
  const assign = assignmentForAmcqRegrade(assignment, savedQuestions);
  const pool = getAmcqQuestionsForScoring(assign);
  const byStem = collectStudentResponses(stored);

  const rawCorrect = [];
  const rawIncorrect = [];

  for (const [stem, { letter, order }] of byStem) {
    const q = findQuestionInPool(pool, stem);
    const correctLetter = String(q?.correctChoice || 'a').toLowerCase();
    const sel = letter;
    if (!sel) continue;
    if (sel === correctLetter) {
      rawCorrect.push({ question: stem, correctChoice: sel, order });
    } else {
      rawIncorrect.push({
        question: stem,
        chosenAnswer: sel,
        correctAnswer: correctLetter,
        order,
      });
    }
  }

  const irtOn = assign.irtScoring === true;
  let pct;
  let irtExtras = {};
  if (irtOn) {
    const bundle = irtScoreBundle(assignmentForIrt(assign), rawCorrect, rawIncorrect);
    pct = bundle.squareScore;
    irtExtras = {
      scoringMode: 'irt_eap_2pl',
      irtTheta: bundle.theta,
      irtSe: bundle.se,
      mstRouteHint: bundle.routeHint,
    };
  } else {
    const t = rawCorrect.length + rawIncorrect.length;
    pct = t === 0 ? 0 : Math.round((rawCorrect.length / t) * 100);
  }

  const correctQuestions = rawCorrect.map((row) => {
    const orig = findQuestionInPool(pool, row.question);
    return formatAmcqGradedQuestion(orig, row.correctChoice, { order: row.order });
  });
  const incorrectQuestions = rawIncorrect.map((row) => {
    const orig = findQuestionInPool(pool, row.question);
    return formatAmcqGradedQuestion(orig, row.chosenAnswer, { order: row.order });
  });

  return {
    correctQuestions,
    incorrectQuestions,
    SquareScore: pct,
    ...irtExtras,
  };
}

function finalizeQuestionStats(questionStats) {
  Object.keys(questionStats).forEach((questionId) => {
    const stats = questionStats[questionId];
    stats.percentageCorrect =
      stats.totalAttempts > 0 ? Math.round((stats.correctCount / stats.totalAttempts) * 100) : 0;
    const choiceStats = {};
    Object.entries(stats.choiceDistribution || {}).forEach(([choice, count]) => {
      choiceStats[choice] = {
        count,
        percentage: stats.totalAttempts > 0 ? Math.round((count / stats.totalAttempts) * 100) : 0,
      };
    });
    stats.choiceDistribution = choiceStats;
  });
  return questionStats;
}

function accumulateQuestionStats(questionStats, recalc) {
  const allQuestions = [...(recalc.correctQuestions || []), ...(recalc.incorrectQuestions || [])];
  for (const question of allQuestions) {
    const qid = question.questionId || question.question;
    if (!qid) continue;
    if (!questionStats[qid]) {
      questionStats[qid] = {
        totalAttempts: 0,
        correctCount: 0,
        choiceDistribution: {},
      };
    }
    const stats = questionStats[qid];
    stats.totalAttempts += 1;
    const choiceKey =
      question.selectedChoice != null && question.selectedChoice !== ''
        ? question.selectedChoice
        : 'blank';
    if (!stats.choiceDistribution[choiceKey]) {
      stats.choiceDistribution[choiceKey] = 0;
    }
    stats.choiceDistribution[choiceKey] += 1;
    if (question.selectedChoice === question.correctChoice) {
      stats.correctCount += 1;
    }
  }
}

const MAX_WRITES_PER_BATCH = 450;

async function commitUpdateBatches(db, updates) {
  for (let i = 0; i < updates.length; i += MAX_WRITES_PER_BATCH) {
    const batch = writeBatch(db);
    const slice = updates.slice(i, i + MAX_WRITES_PER_BATCH);
    for (const { ref, data } of slice) {
      batch.update(ref, data);
    }
    await batch.commit();
  }
}

/**
 * Re-score every AMCQ grade and progress doc for this assignment, update embedded class grades,
 * and refresh assignment question statistics / class average.
 */
export async function syncAmcqGradesAndProgressAfterQuestionBankEdit(db, params) {
  const { assignmentId, classId, assignment, savedQuestions } = params;
  if (!db || !assignmentId || !assignment || !Array.isArray(savedQuestions)) return;

  const assignmentRef = doc(db, 'assignments', assignmentId);
  const questionStats = {};
  const gradeUpdates = [];
  const studentUpdates = [];
  const progressUpdates = [];

  let totalSubmittedScore = 0;
  let submittedGradeCount = 0;

  const gradesSnap = await getDocs(
    query(collection(db, 'grades'), where('assignmentId', '==', assignmentId))
  );

  gradesSnap.forEach((gradeDoc) => {
    const data = gradeDoc.data();
    if (data.format !== 'AMCQ') return;
    const recalc = recalculateAmcqSubmissionFromAssignment(assignment, savedQuestions, data);
    gradeUpdates.push({
      ref: gradeDoc.ref,
      data: {
        correctQuestions: recalc.correctQuestions,
        incorrectQuestions: recalc.incorrectQuestions,
        SquareScore: recalc.SquareScore,
        ...(recalc.scoringMode ? { scoringMode: recalc.scoringMode } : {}),
        ...(typeof recalc.irtTheta === 'number' ? { irtTheta: recalc.irtTheta } : {}),
        ...(typeof recalc.irtSe === 'number' ? { irtSe: recalc.irtSe } : {}),
        ...(recalc.mstRouteHint ? { mstRouteHint: recalc.mstRouteHint } : {}),
      },
    });
    accumulateQuestionStats(questionStats, recalc);
    if (data.submittedAt && typeof recalc.SquareScore === 'number' && !Number.isNaN(recalc.SquareScore)) {
      totalSubmittedScore += recalc.SquareScore;
      submittedGradeCount += 1;
    }
    const uid = data.studentUid;
    const cid = data.classId || classId;
    if (uid && cid) {
      studentUpdates.push({
        ref: doc(db, 'students', uid),
        data: {
          [`class_${cid}.grades.${assignmentId}.score`]: recalc.SquareScore,
        },
      });
    }
  });

  if (classId) {
    const classSnap = await getDoc(doc(db, 'classes', classId));
    const participants = classSnap.exists() ? classSnap.data()?.participants || [] : [];
    const progressPayloads = await Promise.all(
      participants.map(async (p) => {
        if (!p?.uid) return null;
        const progressRef = doc(db, 'assignments(progress)', `${assignmentId}_${p.uid}`);
        const pr = await getDoc(progressRef);
        if (!pr.exists()) return null;
        const pdata = pr.data();
        if (pdata.format !== 'AMCQ') return null;
        if (pdata.status === 'submitted') return null;
        const recalc = recalculateAmcqSubmissionFromAssignment(assignment, savedQuestions, pdata);
        accumulateQuestionStats(questionStats, recalc);
        return {
          ref: progressRef,
          data: {
            correctQuestions: recalc.correctQuestions,
            incorrectQuestions: recalc.incorrectQuestions,
            SquareScore: recalc.SquareScore,
            ...(recalc.scoringMode ? { scoringMode: recalc.scoringMode } : {}),
            ...(typeof recalc.irtTheta === 'number' ? { irtTheta: recalc.irtTheta } : {}),
            ...(typeof recalc.irtSe === 'number' ? { irtSe: recalc.irtSe } : {}),
            ...(recalc.mstRouteHint ? { mstRouteHint: recalc.mstRouteHint } : {}),
          },
        };
      })
    );
    progressPayloads.filter(Boolean).forEach((u) => progressUpdates.push(u));
  }

  await commitUpdateBatches(db, [...gradeUpdates, ...progressUpdates, ...studentUpdates]);

  const finalizedStats = finalizeQuestionStats(questionStats);
  const assignmentPatch = {};
  if (Object.keys(finalizedStats).length > 0) {
    assignmentPatch.questionStatistics = finalizedStats;
  }
  if (submittedGradeCount > 0) {
    assignmentPatch.classAverage = Math.round(totalSubmittedScore / submittedGradeCount);
  }
  if (Object.keys(assignmentPatch).length > 0) {
    await updateDoc(assignmentRef, assignmentPatch);
  }
}
